import React, { useEffect, useState } from 'react';
import { BgRow, Container } from '@infoods/lifebase-ui';

import { observer } from 'mobx-react';
import { AxiosError } from 'axios';

import classes from './NgzInBodyScanIq.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { useStepper } from '../../../shared/hooks/useStepper';
import { WelcomeStep } from './containers/WelcomeStep/WelcomeStep';
import { TwoFAStep } from './containers/TwoFAStep/TwoFAStep';
import { getAccessParamsFromUrl } from '../../../shared/utils/apiUtils';
import { entitySignUpStore } from '../../../shared/stores/entity/entitySignUpStore';
import { scanIqStore } from '../../stores/scanIqStore';
import { CreateCustomerStep } from './containers/CreateCustomerStep/CreateCustomerStep';
import { ExistCustomerStep } from './containers/ExistCustomerStep/ExistCustomerStep';
import { SETUP_STATUS } from '../../interfaces/ScanIqCustomerSetupInfo';
import { UnavailableWhenLoggedIn } from '../../../shared/containers/UnavailableWhenLoggedIn/UnavailableWhenLoggedIn';
import { entitySignUpService } from '../../../shared/services/entity/entitySignUpService';
import { ErrorState } from '../../../shared/components/ErrorState/ErrorState';
import { RESPONSE_STATUSES } from '../../../shared/constants/RESPONSE_STATUSES';
import { twoFAService, VERIFICATION_CHANNEL } from '../../../auth-management';
import { SwymoHeader } from '../../../shared/components/header-components/SwymoHeader/SwymoHeader';

/**
 * ScanIQ customer setup flow. Unauthenticated only — conflicts with a logged-in cookie.
 *
 * Welcome → 2FA verify (backend creates the customer record here, so PersonalInfoStep
 * later only updates it) → setup step, branched by the initial setupStatus captured
 * at 2FA: COMPLETED → ExistCustomerStep, NEED_SETUP → CreateCustomerStep (which itself
 * swaps step 2 between SelectSubscription and SelfBillingPlanStep based on the entity's
 * scanIqBillingType).
 *
 * The branch reads `initialCustomer` (frozen at 2FA), not the live `customer`: the
 * PersonalInfoStep response may flip setupStatus to COMPLETED and would otherwise
 * remount step 2 mid-flow.
 */
const NgzInBodyScanIq = observer(() => {
  const { initialCustomer, setDefaultValues, setCustomer, setInitialCustomer } =
    scanIqStore;
  const { entityId, setEntityData } = entitySignUpStore;

  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  const { activeStep, goToNextStep } = useStepper();

  const { accessParams, allParamsExist } = getAccessParamsFromUrl();
  const [entityIsInactive, setEntityIsInactive] = useState(false);

  const getEntityInfo = async () => {
    if (allParamsExist) {
      try {
        const entityData = await entitySignUpService.getScanIqEntityData(accessParams, {
          ignoreErrorCodes: [RESPONSE_STATUSES.INVALID],
        });

        setEntityData(entityData);
      } catch (error: unknown) {
        if (
          error instanceof AxiosError &&
          error.response?.status === RESPONSE_STATUSES.INVALID
        ) {
          setEntityIsInactive(true);
          return;
        }
        throw error;
      }
    }
  };

  const handleVerifyCode = async (consumer: string, code: string) => {
    if (!entityId) {
      throw new Error('Entity ID is not defined');
    }

    const scanIqCustomer = await twoFAService.verifyCodeForScanIq(
      {
        to: consumer,
        code,
        targetId: entityId,
      },
      { ignoreErrorCodes: [RESPONSE_STATUSES.VERIFICATION_CODE_INCORRECT] },
    );

    setInitialCustomer(scanIqCustomer);
    setCustomer(scanIqCustomer);
    goToNextStep();
  };

  useEffect(() => {
    getEntityInfo();

    return setDefaultValues;
  }, []);

  if (!allParamsExist) {
    return (
      <ErrorState
        title="We couldn't find this page"
        description={
          <p>
            Looks like the link might be incomplete or incorrect. Please double-check
            <br />
            the address and try again — or copy the full link from the original message.
          </p>
        }
      />
    );
  }

  if (entityIsInactive) {
    return (
      <ErrorState
        title="This entity isn't ready to accept customers yet"
        description={
          <p>
            It looks like this entity hasn&apos;t activated their account for selling
            services.
            <br />
            Please check back later or contact them directly if you have any questions.
          </p>
        }
      />
    );
  }

  const stepsComponents = [
    <WelcomeStep
      key="welcome-step"
      onSubmit={async (data) => {
        setPhoneNumber(data.phoneNumber);

        // This extra request is needed to ensure that the phone number is valid before moving the user to the next step.
        // We send the same request on the next step to get the verification code expiration time, but it does not trigger a second SMS while the current code has not expired.
        await twoFAService.sendVerificationCode(
          data.phoneNumber,
          VERIFICATION_CHANNEL.SMS,
        );

        goToNextStep();
      }}
    />,
    <TwoFAStep
      key="two-fa-step"
      phoneNumber={phoneNumber}
      onVerifyCode={handleVerifyCode}
    />,
    initialCustomer?.setupStatus === SETUP_STATUS.COMPLETED ? (
      <ExistCustomerStep key="exist-customer-step" />
    ) : (
      <CreateCustomerStep key="create-customer-step" />
    ),
  ];

  return (
    <UnavailableWhenLoggedIn>
      <SwymoHeader inAngularContainer />
      <Container maxWidth="sm">
        <BgRow
          background={
            <div
              className={classes.backgroundImg}
              role="img"
              aria-label="inbody scaniq background"
            />
          }
        >
          {stepsComponents[activeStep]}
        </BgRow>
      </Container>
    </UnavailableWhenLoggedIn>
  );
});

angularizeWithProviders(NgzInBodyScanIq, 'ngzInBodyScanIq', {});

export { NgzInBodyScanIq };
